/** Group accounts (phase 5b). Mirrors Contracts/Practice/Phase6Group. Dates arrive as ISO yyyy-MM-dd. */

export enum GroupStatus {
  Draft = 0,
  Ready = 1,
  Finalised = 2,
  Superseded = 3,
}

export enum GroupUndertakingType {
  Parent = 0,
  Subsidiary = 1,
  Associate = 2,
  JointVenture = 3,
  Subgroup = 4,
}

export enum GroupInclusionStatus {
  Included = 0,
  Excluded = 1,
  EquityAccounted = 2,
}

export enum GroupEventType {
  Acquisition = 0,
  Disposal = 1,
  OwnershipChange = 2,
}

export enum GroupColumnType {
  Parent = 0,
  Undertaking = 1,
  GroupAdjustments = 2,
}

/** Group journal status, matching ap.GroupJournal.Status. */
export enum GroupJournalStatus {
  Draft = 0,
  Posted = 1,
  Reversed = 2,
  Reversal = 3,
}

export interface GroupSummaryRow {
  groupPeriodId: string;
  parentClientId: string;
  parentName: string;
  parentAccountingPeriodId: string;
  groupName: string;
  startDate: string;
  endDate: string;
  periodLabel: string;
  status: GroupStatus;
  statusLabel: string;
  isStale: boolean;
  staleReason: string | null;
  balanceVersion: number;
  lastRebuiltAtUtc: string | null;
  readyAtUtc: string | null;
  finalisedAtUtc: string | null;
  revisionOfGroupPeriodId: string | null;
  revisionNo: number;
  revisionReason: string | null;
  undertakingCount: number;
  rowVersion: string | null;
}

export interface GroupUndertakingRow {
  groupUndertakingId: string;
  groupPeriodId: string;
  clientId: string;
  clientName: string;
  accountingPeriodId: string | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
  periodLabel: string;
  periodStatus: number | null;
  parentUndertakingId: string | null;
  undertakingType: GroupUndertakingType;
  undertakingTypeLabel: string;
  ownershipPercent: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  inclusionStatus: GroupInclusionStatus;
  inclusionStatusLabel: string;
  exclusionReason: string | null;
  acquisitionDate: string | null;
  disposalDate: string | null;
  sortOrder: number;
  /** False when the undertaking's own period does not overlap the group period. */
  isPeriodCompatible: boolean;
  /** False until the undertaking's period has left draft and its trial balance is built. */
  isReady: boolean;
  rowVersion: string | null;
}

export interface GroupEventRow {
  groupEventId: string;
  groupPeriodId: string;
  groupUndertakingId: string;
  clientName: string;
  eventType: GroupEventType;
  eventTypeLabel: string;
  eventDate: string;
  ownershipBefore: number;
  ownershipAfter: number;
  consideration: number | null;
  disclosureText: string | null;
}

export interface GroupJournalTypeOption {
  typeCode: string;
  description: string;
  isElimination: boolean;
}

export interface GroupStructureView {
  group: GroupSummaryRow;
  undertakings: GroupUndertakingRow[];
  events: GroupEventRow[];
  journalTypes: GroupJournalTypeOption[];
  /** What still blocks approval; empty means the group can be marked ready. */
  readinessIssues: string[];
}

export interface CreateGroupPayload {
  parentClientId: string;
  parentAccountingPeriodId: string;
  groupName: string;
  startDate: string;
  endDate: string;
}

export interface SaveUndertakingPayload {
  groupPeriodId: string;
  groupUndertakingId?: string | null;
  clientId: string;
  accountingPeriodId?: string | null;
  parentUndertakingId?: string | null;
  undertakingType: GroupUndertakingType;
  ownershipPercent: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  inclusionStatus: GroupInclusionStatus;
  exclusionReason?: string | null;
  acquisitionDate?: string | null;
  disposalDate?: string | null;
  sortOrder: number;
  rowVersion?: string | null;
}

export interface SaveGroupEventPayload {
  groupPeriodId: string;
  groupEventId?: string | null;
  groupUndertakingId: string;
  eventType: GroupEventType;
  eventDate: string;
  ownershipBefore: number;
  ownershipAfter: number;
  consideration?: number | null;
  disclosureText?: string | null;
}

export interface GroupTrialBalanceColumn {
  columnKey: string;
  columnType: GroupColumnType;
  columnName: string;
  ownershipPercent: number | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

export interface GroupTrialBalanceCell {
  columnKey: string;
  amount: number;
  priorAmount: number;
}

export interface GroupTrialBalanceRow {
  reportingCodeId: string;
  code: string;
  description: string;
  statementArea: number;
  normalSign: number;
  cells: GroupTrialBalanceCell[];
  consolidatedAmount: number;
  consolidatedPriorAmount: number;
}

/** The consolidated total must equal the undertakings plus the group/elimination journals. */
export interface GroupTrialBalanceTotals {
  undertakingsTotal: number;
  groupAdjustmentsTotal: number;
  consolidatedTotal: number;
  reconciles: boolean;
  isBalanced: boolean;
}

export interface GroupTrialBalanceView {
  groupPeriodId: string;
  groupName: string;
  parentName: string;
  periodLabel: string;
  status: GroupStatus;
  isStale: boolean;
  staleReason: string | null;
  balanceVersion: number;
  lastRebuiltAtUtc: string | null;
  columns: GroupTrialBalanceColumn[];
  rows: GroupTrialBalanceRow[];
  totals: GroupTrialBalanceTotals;
}

export interface GroupJournalRow {
  groupJournalId: string;
  groupPeriodId: string;
  journalNo: number;
  typeCode: string;
  typeDescription: string;
  isElimination: boolean;
  journalDate: string;
  narrative: string | null;
  status: GroupJournalStatus;
  statusLabel: string;
  reversalOfGroupJournalId: string | null;
  reversedByGroupJournalId: string | null;
  postedAtUtc: string | null;
  postingComment: string | null;
  totalDebit: number;
  totalCredit: number;
  isBalanced: boolean;
  rowVersion: string | null;
}

export interface GroupJournalLineRow {
  groupJournalLineId: string;
  lineNo: number;
  reportingCodeId: string;
  reportingCode: string | null;
  reportingCodeDescription: string | null;
  groupUndertakingId: string | null;
  undertakingName: string | null;
  debit: number;
  credit: number;
  narrative: string | null;
}

export interface GroupJournalView {
  journal: GroupJournalRow;
  lines: GroupJournalLineRow[];
}

export interface SaveGroupJournalLine {
  lineNo: number;
  reportingCodeId: string;
  /** Null posts the line to the group adjustments/eliminations column. */
  groupUndertakingId?: string | null;
  debit: number;
  credit: number;
  narrative?: string | null;
}

export interface SaveGroupJournalPayload {
  groupPeriodId: string;
  groupJournalId?: string | null;
  typeCode: string;
  journalDate: string;
  narrative?: string | null;
  lines: SaveGroupJournalLine[];
  rowVersion?: string | null;
}

export interface PostGroupJournalPayload {
  groupPeriodId: string;
  groupJournalId: string;
  comment?: string | null;
  rowVersion?: string | null;
}

export interface ReverseGroupJournalPayload {
  groupPeriodId: string;
  groupJournalId: string;
  comment?: string | null;
}

export interface ReviseGroupPayload {
  groupPeriodId: string;
  reason: string;
}

export interface UnfinaliseGroupPayload {
  groupPeriodId: string;
  reason?: string | null;
}

export interface GroupExportPayload {
  groupPeriodId: string;
  format: number;
  isPrint?: boolean;
}
